import React from "react";

const LAST_UPDATED = "29 October 2026";
const CONTACT_EMAIL = "support@planbjobs.in"; // change to your real email
const COMPANY = "PlanB Foundation"; // change if your legal name differs

const styles = {
  page: { background: "#f7f8fa", minHeight: "100vh", padding: "32px 16px" },
  card: {
    maxWidth: 820,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 12,
    padding: "32px 28px",
    boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
    color: "#222",
    lineHeight: 1.7,
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },
  h1: { fontSize: 30, marginBottom: 4 },
  h2: { fontSize: 20, marginTop: 28, marginBottom: 8 },
  muted: { color: "#666", fontSize: 14 },
};

export default function PrivacyPolicy() {
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.h1}>Privacy Policy</h1>
        <p style={styles.muted}>Last updated: {LAST_UPDATED}</p>

        <p>
          This Privacy Policy explains how {COMPANY} ("we", "us", "our") collects, uses and
          protects your information when you use the PlanB Jobs website and the PlanB: AI Job
          Search mobile app (together, the "Service"). By using the Service you agree to this
          policy.
        </p>

        <h2 style={styles.h2}>1. Information we collect</h2>
        <p>Depending on how you use the Service, we may collect:</p>
        <ul>
          <li>
            <strong>Account details:</strong> name, gender, mobile number (verified by a one-time SMS OTP), email address, Google account name and email if you sign in with Google, password
            (stored in encrypted form) and your role (job seeker or employer).
          </li>
          <li>
            <strong>Profile and resume details:</strong> education, work experience, skills,
            current and expected salary, preferred job role and location, resume file, and
            profile photo if you upload one.
          </li>
          <li>
            <strong>Employer details:</strong> company name, contact person, company contact
            information and the jobs you post.
          </li>
          <li>
            <strong>Application activity:</strong> jobs you view, save or apply to, and
            applications received by employers.
          </li>
          <li>
            <strong>Device information:</strong> device type, operating system, app version and a
            push notification token so we can send you notifications.
          </li>
          <li>
            <strong>Usage data:</strong> IP address, pages or screens visited and error logs,
            used to keep the Service secure and working.
          </li>
        </ul>

        <h2 style={styles.h2}>2. How we use your information</h2>
        <ul>
          <li>To create and manage your account and let you log in.</li>
          <li>To match job seekers with relevant jobs and let employers find candidates.</li>
          <li>
            To share a job seeker's profile and resume with an employer when the job seeker
            applies to that employer's job.
          </li>
          <li>
            To send notifications, SMS, email or WhatsApp messages about job alerts,
            applications and account activity.
          </li>
          <li>To provide support, prevent fraud and misuse, and improve the Service.</li>
          <li>To comply with legal obligations.</li>
        </ul>

        <h2 style={styles.h2}>3. Who can see your information</h2>
        <p>
          Employers can see the profile and resume details of job seekers who apply to their
          jobs. We do not sell your personal information. We may share information with service
          providers who help us run the Service (such as hosting, push notifications, email,
          SMS and WhatsApp messaging), and with authorities when required by law.
        </p>

        <h2 style={styles.h2}>4. Permissions used by the app</h2>
        <ul>
          <li>
            <strong>Notifications:</strong> to send job alerts and application updates.
          </li>
          <li>
            <strong>Files / storage:</strong> only when you choose to upload a resume or photo.
          </li>
          <li>
            <strong>Internet access:</strong> to connect to our servers.
          </li>
        </ul>

        <h2 style={styles.h2}>5. Data security</h2>
        <p>
          We use reasonable technical and organisational measures to protect your information,
          including encrypted connections (HTTPS) and hashed passwords. No method of
          transmission or storage is completely secure, so we cannot guarantee absolute
          security.
        </p>

        <h2 style={styles.h2}>6. Data retention</h2>
        <p>
          We keep your information while your account is active and as long as needed to
          provide the Service or meet legal requirements. When you delete your account, we
          delete or anonymise your personal information, except where we must keep it by law.
        </p>

        <h2 style={styles.h2}>7. Your rights and choices</h2>
        <ul>
          <li>You can view and update your profile details in the app or website.</li>
          <li>You can turn off notifications in your device settings.</li>
          <li>
            You can ask us to delete your account and data by emailing{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will respond within a
            reasonable time.
          </li>
        </ul>

        <h2 style={styles.h2}>8. Children's privacy</h2>
        <p>
          The Service is meant for people aged 18 and over. We do not knowingly collect
          information from children under 18. If you believe a child has given us information,
          contact us and we will delete it.
        </p>

        <h2 style={styles.h2}>9. Third-party links and services</h2>
        <p>
          The Service may contain links to other websites, and uses third-party services such
          as Google Sign-In, Google Firebase for notifications, and AI suggestion services (text you type for company, role or skill suggestions may be sent to such a service to return suggestions). These have their own privacy policies, and we
          are not responsible for their practices.
        </p>

        <h2 style={styles.h2}>10. Changes to this policy</h2>
        <p>
          We may update this policy from time to time. The latest version will always be on
          this page with the updated date at the top.
        </p>

        <h2 style={styles.h2}>11. Contact us</h2>
        <p>
          {COMPANY}
          <br />
          Bengaluru, Karnataka, India
          <br />
          Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </div>
    </div>
  );
}